<template>
  <div class="lp-who__grid">
    <LandingAccordion
      ref="accordionRef"
      :items="items"
      id-prefix="lp-who"
      @change="onChange"
    />

    <figure class="lp-who__photo" :style="photoStyle">
      <picture v-for="(row, index) in props.rows" :key="index">
        <source type="image/webp" :srcset="row.imageWebp" />
        <img
          :class="{ 'is-active': activeIndex === index }"
          :src="row.image"
          :alt="row.alt"
          loading="lazy"
          decoding="async"
        />
      </picture>
    </figure>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import LandingAccordion from "./landing-accordion.vue";

interface WhoRow {
  title: string;
  text: string;
  alt: string;
  image: string;
  imageWebp: string;
}

const props = defineProps<{
  rows: WhoRow[];
}>();

const items = computed(() =>
  props.rows.map((row) => ({ title: row.title, text: row.text })),
);

const accordionRef = ref<InstanceType<typeof LandingAccordion>>();
const openIndex = ref<number | null>(null);

/* The photo shown. It follows the row you open and then stays there when you
   close it again — collapsing a row should not throw the photo back to the
   first one. It only starts on 0 because that is what the page opens with. */
const activeIndex = ref(0);

/* ---- Photo height ----
   On desktop the photo spans the closed list exactly, top divider to bottom
   divider. It is measured rather than set as a ratio because the list height
   depends on the copy: a row title wrapping to two lines changes it, and the
   Polish runs longer than the English.

   The measurement is only taken while every row is closed, so opening one
   grows the list without moving the photo — the photo staying put is the whole
   point. Below the single-column breakpoint the height is cleared and the
   stylesheet's aspect-ratio takes over.

   syncPhoto only ever reads the list and writes the figure, so the observer
   cannot feed itself. */
const DESKTOP = 960; // matches the "lg" breakpoint in the stylesheet
const SETTLE = 600; // the copy's 0.55s grid-rows transition, plus a little

const photoHeight = ref<number | null>(null);

/* True while a row is animating open or closed. The ResizeObserver below fires
   on every frame of that animation, and on close openIndex is already null, so
   without this the photo would track the list shrinking — a second animation
   nobody asked for. Opening was never affected: the openIndex guard already
   covered it, which is why only collapsing looked wrong. */
const isAnimating = ref(false);
let settleTimer: ReturnType<typeof setTimeout> | undefined;

const photoStyle = computed(() =>
  photoHeight.value === null ? undefined : { height: `${photoHeight.value}px` },
);

const listEl = (): HTMLElement | undefined => accordionRef.value?.rootRef;

const syncPhoto = (): void => {
  const list = listEl();

  if (!list || window.innerWidth < DESKTOP) {
    photoHeight.value = null;
    return;
  }

  if (openIndex.value !== null || isAnimating.value) {
    return; // mid-animation, or a row is open: keep the closed height
  }

  photoHeight.value = list.offsetHeight;
};

const onChange = (open: number | null): void => {
  openIndex.value = open;

  if (open !== null) {
    activeIndex.value = open;
  }

  // Hold the current height until the list has finished moving, then take a
  // fresh measurement. Closing returns the list to the height it already had,
  // so in practice that settling measurement changes nothing — it is there so
  // a genuine content or width change still gets picked up.
  isAnimating.value = true;
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    isAnimating.value = false;
    syncPhoto();
  }, SETTLE);
};

let observer: ResizeObserver | undefined;

onMounted(() => {
  syncPhoto();

  // The webfonts land after mount and change the row heights with them, so
  // measure again once they have. The ResizeObserver below normally catches
  // this, but it is not guaranteed to have been attached in time.
  document.fonts?.ready.then(syncPhoto);

  const list = listEl();
  if ("ResizeObserver" in window && list) {
    observer = new ResizeObserver(() => syncPhoto());
    observer.observe(list);
  }

  window.addEventListener("resize", syncPhoto);
});

onBeforeUnmount(() => {
  clearTimeout(settleTimer);
  observer?.disconnect();
  window.removeEventListener("resize", syncPhoto);
});
</script>
